import ApplicationController from "./application_controller";

// Manages the dynamic Google Sheets export configuration form.
//
// Handles:
//   - Adding / removing sheet blocks
//   - Switching sheet mode (all / domains / single) and showing/hiding the correct fields
//   - Adding / removing column rows within a sheet block
//   - Auto-uppercasing spreadsheet column letter inputs
//
export default class SheetsExportConfigController extends ApplicationController {
  static targets = ['sheetsContainer', 'sheetBlock', 'columnsBody', 'columnRow', 'domainsBody', 'domainRow']

  // ─── Sheet blocks ───────────────────────────────────────────────────────────

  addSheet() {
    const container = this.sheetsContainerTarget;
    const nextIdx   = container.querySelectorAll('.sheet-block').length;
    const template  = document.getElementById('sheet-block-template');
    if (!template) { return; }

    const html      = template.innerHTML.replace(/SHEET_IDX/g, nextIdx);
    const wrapper   = document.createElement('div');
    wrapper.innerHTML = html;
    const block     = wrapper.firstElementChild;

    container.appendChild(block);
    this.updateSheetNumbers();
  }

  removeSheet(event) {
    const block = event.currentTarget.closest('.sheet-block');
    if (!block) { return; }

    const container = this.sheetsContainerTarget;
    if (container.querySelectorAll('.sheet-block').length <= 1) {
      alert('At least one sheet block is required.');
      return;
    }

    block.remove();
    this.reindexSheets();
    this.updateSheetNumbers();
  }

  // ─── Mode toggle ────────────────────────────────────────────────────────────

  modeChanged(event) {
    const radio = event.currentTarget;
    const block = radio.closest('.sheet-block');
    if (!block) { return; }

    const mode = radio.value;

    // Show/hide mode-specific fields
    block.querySelectorAll('.mode-fields').forEach(el => {
      if (el.dataset.mode === mode) {
        el.classList.remove('d-none');
      } else {
        el.classList.add('d-none');
      }
    });

    // Highlight the selected mode label card
    block.querySelectorAll('label[for*="_mode_"]').forEach(label => {
      label.classList.remove('border-primary', 'bg-primary', 'bg-opacity-10');
    });
    const selectedLabel = block.querySelector(`label[for="${radio.id}"]`);
    if (selectedLabel) {
      selectedLabel.classList.add('border-primary', 'bg-primary', 'bg-opacity-10');
    }
  }

  // ─── Column rows ────────────────────────────────────────────────────────────

  addColumn(event) {
    const sheetIdx = event.params.sheetIdx;
    const tbody    = document.getElementById(`columns-body-${sheetIdx}`);
    if (!tbody) { return; }

    const nextColIdx = tbody.querySelectorAll('.column-row').length;
    const template   = document.getElementById('column-row-template');
    if (!template) { return; }

    const html       = template.innerHTML
      .replace(/SHEET_IDX/g, sheetIdx)
      .replace(/COL_IDX/g,   nextColIdx);

    const wrapper    = document.createElement('tbody');
    wrapper.innerHTML = html;
    const row        = wrapper.firstElementChild;
    tbody.appendChild(row);
  }

  removeColumn(event) {
    const row   = event.currentTarget.closest('.column-row');
    const tbody = row?.closest('tbody');
    if (!row || !tbody) { return; }

    if (tbody.querySelectorAll('.column-row').length <= 1) {
      alert('At least one column is required.');
      return;
    }

    row.remove();
    this.reindexColumnsInBody(tbody);
  }

  // ─── Domain rows ────────────────────────────────────────────────────────────

  addDomainRow(event) {
    const sheetIdx   = event.params.sheetIdx;
    const tbody        = document.getElementById(`domains-body-${sheetIdx}`);
    if (!tbody) { return; }

    const nextDomainIdx = tbody.querySelectorAll('.domain-row').length;
    const template      = document.getElementById('domain-row-template');
    if (!template) { return; }

    const html = template.innerHTML
      .replace(/SHEET_IDX/g, sheetIdx)
      .replace(/DOMAIN_IDX/g, nextDomainIdx);

    const wrapper = document.createElement('tbody');
    wrapper.innerHTML = html;
    const row = wrapper.firstElementChild;
    tbody.appendChild(row);
  }

  removeDomainRow(event) {
    const row   = event.currentTarget.closest('.domain-row');
    const tbody = row?.closest('tbody');
    if (!row || !tbody) { return; }

    if (tbody.querySelectorAll('.domain-row').length <= 1) {
      alert('At least one domain is required.');
      return;
    }

    row.remove();
    this.reindexDomainsInBody(tbody);
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  uppercaseColumn(event) {
    const input = event.currentTarget;
    const pos   = input.selectionStart;
    input.value = input.value.toUpperCase();
    input.setSelectionRange(pos, pos);
  }

  // Re-indexes all sheet blocks so param names stay sequential (sheets[0], sheets[1], …).
  reindexSheets() {
    const container = this.sheetsContainerTarget;
    const blocks    = container.querySelectorAll('.sheet-block');

    blocks.forEach((block, newIdx) => {
      block.dataset.sheetIdx = newIdx;

      // Update all name attributes within this block
      block.querySelectorAll('[name]').forEach(el => {
        el.name = el.name.replace(/sheets\[\d+\]/g, `sheets[${newIdx}]`);
      });

      // Update column tbody id
      const tbody = block.querySelector('[id^="columns-body-"]');
      if (tbody) {
        tbody.id              = `columns-body-${newIdx}`;
        tbody.dataset.sheetIdx = newIdx;
      }

      const domainsTbody = block.querySelector('[id^="domains-body-"]');
      if (domainsTbody) {
        domainsTbody.id              = `domains-body-${newIdx}`;
        domainsTbody.dataset.sheetIdx = newIdx;
      }

      // Update id / for on mode radios
      block.querySelectorAll('[id]').forEach(el => {
        el.id = el.id.replace(/sheet_\d+_/g, `sheet_${newIdx}_`);
      });
      block.querySelectorAll('[for]').forEach(el => {
        el.htmlFor = el.htmlFor.replace(/sheet_\d+_/g, `sheet_${newIdx}_`);
      });

      // Update data-sheet-idx on mode-fields divs
      block.querySelectorAll('.mode-fields').forEach(el => {
        el.dataset.sheetIdx = newIdx;
      });

      // Update action params for addColumn buttons
      block.querySelectorAll('[data-action*="addColumn"]').forEach(btn => {
        btn.dataset['sheetsExportConfigSheetIdxParam'] = newIdx;
      });

      block.querySelectorAll('[data-action*="addDomainRow"]').forEach(btn => {
        btn.dataset['sheetsExportConfigSheetIdxParam'] = newIdx;
      });
    });
  }

  reindexDomainsInBody(tbody) {
    const rows = tbody.querySelectorAll('.domain-row');

    rows.forEach((row, newDomainIdx) => {
      row.querySelectorAll('[name]').forEach(el => {
        el.name = el.name.replace(/\]\[domain_entries\]\[\d+\]/g, `][domain_entries][${newDomainIdx}]`);
      });
    });
  }

  // Re-indexes column rows in a tbody after a row is removed.
  reindexColumnsInBody(tbody) {
    const sheetIdx = tbody.dataset.sheetIdx;
    const rows     = tbody.querySelectorAll('.column-row');

    rows.forEach((row, newColIdx) => {
      row.querySelectorAll('[name]').forEach(el => {
        el.name = el.name.replace(/\]\[columns\]\[\d+\]/g, `][columns][${newColIdx}]`);
      });
    });
  }

  updateSheetNumbers() {
    const blocks = this.sheetsContainerTarget.querySelectorAll('.sheet-block');
    blocks.forEach((block, idx) => {
      const numEl = block.querySelector('.sheet-block-number');
      if (numEl) { numEl.textContent = idx + 1; }
    });
  }
}
